import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip } from '@mui/material';
import { ZoomIn, ZoomOut, FitScreen } from '@mui/icons-material';
import { getUnitSymbol } from './utils/unitHelpers';

// Format number with minimum decimal places (up to 3)
const formatDimension = (num) => {
  // Handle null/undefined values
  if (num == null || isNaN(num)) {
    return '0';
  }
  // Convert to 3 decimal places first
  const fixed3 = Number(num.toFixed(3));
  // Convert back to string
  let result = fixed3.toString();
  
  // Only remove trailing zeros after a decimal point
  // Example: 20.000 -> 20, 20.5 -> 20.5, 20.500 -> 20.5
  if (result.includes('.')) {
    result = result.replace(/\.?0+$/, '');
  }
  
  return result;
};

// Coordinate transformation helpers
const transformCoordinates = (baseX, baseY, baseWidth, baseLength, rotation, stockSheet) => {
  if (rotation === 90) {
    return {
      x: baseY,
      y: stockSheet.width - baseX - baseWidth,
      width: baseLength,
      length: baseWidth
    };
  }
  return {
    x: baseX,
    y: baseY,
    width: baseWidth,
    length: baseLength
  };
};

const getSheetDimensions = (rotation, stockSheet) => {
  if (rotation === 90) {
    return {
      width: stockSheet.length,
      height: stockSheet.width
    };
  }
  return {
    width: stockSheet.width,
    height: stockSheet.length
  };
};

const getSubsheetBounds = (subsheet, rotation, stockSheet) => {
  if (!subsheet) return null;
  
  const subsheetX = subsheet.x_position ?? 0;
  const subsheetY = subsheet.y_position ?? 0;
  const subsheetWidth = subsheet.width ?? 0;
  const subsheetLength = subsheet.length ?? 0;
  
  if (rotation === 90) {
    return {
      x: subsheetY,
      y: stockSheet.width - (subsheetX + subsheetWidth),
      width: subsheetLength,
      height: subsheetWidth,
      // Keep original for calculations
      originalX: subsheetX,
      originalY: subsheetY,
      originalWidth: subsheetWidth,
      originalLength: subsheetLength
    };
  }
  
  return {
    x: subsheetX,
    y: subsheetY,
    width: subsheetWidth,
    height: subsheetLength,
    // Keep original for calculations
    originalX: subsheetX,
    originalY: subsheetY,
    originalWidth: subsheetWidth,
    originalLength: subsheetLength
  };
};

const transformCutCoordinates = (cut, rotation, stockSheet, subsheet = null) => {
  let x1, y1, x2, y2;
  
  // Get base coordinates from cut
  if (cut.type === 'horizontal') {
    x1 = cut.minX ?? 0;
    x2 = cut.maxX ?? stockSheet.width;
    y1 = cut.position;
    y2 = cut.position;
  } else {
    x1 = cut.position;
    x2 = cut.position;
    y1 = cut.minY ?? 0;
    y2 = cut.maxY ?? stockSheet.length;
  }
  
  // Limit to subsheet bounds if provided
  if (subsheet) {
    const bounds = getSubsheetBounds(subsheet, rotation, stockSheet);
    if (bounds) {
      if (cut.type === 'horizontal') {
        x1 = Math.max(bounds.originalX, x1);
        x2 = Math.min(bounds.originalX + bounds.originalWidth, x2);
      } else {
        y1 = Math.max(bounds.originalY, y1);
        y2 = Math.min(bounds.originalY + bounds.originalLength, y2);
      }
    }
  }
  
  // Apply rotation
  if (rotation === 90) {
    if (cut.type === 'horizontal') {
      // Horizontal cut becomes vertical when rotated
      return {
        x1: cut.position,
        y1: stockSheet.width - x2,
        x2: cut.position,
        y2: stockSheet.width - x1
      };
    } else {
      // Vertical cut becomes horizontal when rotated
      return {
        x1: y1,
        y1: stockSheet.width - cut.position,
        x2: y2,
        y2: stockSheet.width - cut.position
      };
    }
  }
  
  return { x1, y1, x2, y2 };
};

// Helper function to limit cut to subsheet bounds
const limitCutToSubsheet = (cut, subsheet, stockSheet) => {
  if (!subsheet) return cut;
  
  const subsheetX = subsheet.x_position ?? 0;
  const subsheetY = subsheet.y_position ?? 0;
  const subsheetWidth = subsheet.width ?? 0;
  const subsheetLength = subsheet.length ?? 0;
  
  return {
    ...cut,
    minX: Math.max(subsheetX, cut.minX ?? 0),
    maxX: Math.min(subsheetX + subsheetWidth, cut.maxX ?? stockSheet.width),
    minY: Math.max(subsheetY, cut.minY ?? 0),
    maxY: Math.min(subsheetY + subsheetLength, cut.maxY ?? stockSheet.length)
  };
};

const CutLayoutVisualization = ({ stockSheet: stockSheetProp, index, fontSize = 3, showDimensions = true, showLabels = true, rotation = 0, showCutOrder = false, showRotationIndicator = true, unitSystem = 'imperial', inputPanels = [], responsePanels = [], hoveredCut: externalHoveredCut = null, onHoveredCutChange, themeMode = 'dark' }) => {
  // ALWAYS call hooks first, before any returns
  const [internalHoveredCut, setInternalHoveredCut] = useState(null);
  
  // Use external hoveredCut if provided, otherwise use internal state
  const hoveredCut = externalHoveredCut !== null ? externalHoveredCut : internalHoveredCut;
  const setHoveredCut = (value) => {
    if (onHoveredCutChange) {
      onHoveredCutChange(value);
    } else {
      setInternalHoveredCut(value);
    }
  };

  // Theme-aware colors
  const isDark = themeMode === 'dark';
  const bgColor = isDark ? '#0d1117' : '#ffffff';
  const borderColor = isDark ? '#30363d' : '#d0d7de';
  const textColor = isDark ? '#ffffff' : '#24292f';
  const textSecondaryColor = isDark ? '#8b949e' : '#57606a';
  const nullPanelColor = isDark ? '#30363d' : '#d0d7de';
  const wastePatternColor = isDark ? '#1f4a5a' : '#8b949e';
  const wasteStrokeColor = isDark ? '#1f4a5a' : '#8b949e';
  const exteriorBorderColor = isDark ? '#333' : '#24292f';
  const gridLineColor = isDark ? '#999999' : '#8b949e';
  const cutLineColor = isDark ? '#ff0000' : '#d32f2f';
  const cutTextColor = isDark ? '#000000' : '#ffffff';
  
  // PDF export colors - always use light mode
  const pdfTextColor = '#24292f'; // Light mode text color
  const [hoveredPanel, setHoveredPanel] = useState(null);
  const [svgMaxHeight, setSvgMaxHeight] = useState('80vh');
  
  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  
  // Update max height based on screen size to ensure entire sheet is visible
  useEffect(() => {
    const updateMaxHeight = () => {
      if (window.innerWidth < 900) {
        // On small screens, use calc to account for header, stats table, and some padding
        // Increase the offset to ensure entire sheet fits (account for more space needed)
        setSvgMaxHeight('calc(100vh - 350px)');
      } else {
        // On larger screens, use 80vh to ensure entire sheet fits
        setSvgMaxHeight('80vh');
      }
    };
    
    updateMaxHeight();
    window.addEventListener('resize', updateMaxHeight);
    return () => window.removeEventListener('resize', updateMaxHeight);
  }, []);
  
  // Cleanup panning on unmount (must be before early return)
  useEffect(() => {
    if (!stockSheetProp) return; // Early exit if no data
    
    const handleGlobalMouseUp = () => setIsPanning(false);
    const handleGlobalMouseMove = (e) => {
      if (isPanning && containerRef.current && panStart.x !== undefined) {
        const stockSheet = {
          width: stockSheetProp.statistics?.sheet_size?.width || 0,
          length: stockSheetProp.statistics?.sheet_size?.length || 0,
        };
        const baseViewBoxWidth = rotation === 90 ? stockSheet.length + 8 : stockSheet.width + 8;
        const baseViewBoxHeight = rotation === 90 ? stockSheet.width + 8 : stockSheet.length + 8;
        const rect = containerRef.current.getBoundingClientRect();
        const currentViewBoxWidth = baseViewBoxWidth / zoom;
        const currentViewBoxHeight = baseViewBoxHeight / zoom;
        
        // Calculate delta in screen pixels
        const deltaScreenX = e.clientX - panStart.x;
        const deltaScreenY = e.clientY - panStart.y;
        
        // Convert screen delta to SVG coordinate delta
        const deltaX = (deltaScreenX / rect.width) * currentViewBoxWidth;
        const deltaY = (deltaScreenY / rect.height) * currentViewBoxHeight;
        
        setPanX(panStart.panX - deltaX);
        setPanY(panStart.panY - deltaY);
      }
    };
    
    if (isPanning) {
      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalMouseUp);
      window.addEventListener('mouseleave', handleGlobalMouseUp);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('mouseleave', handleGlobalMouseUp);
    };
  }, [isPanning, panStart, zoom, rotation, stockSheetProp]);
  
  // Extract data from stockSheet prop
  if (!stockSheetProp) {
    console.error('CutLayoutVisualization: stockSheet prop is required');
    return <div>Error: Stock sheet data is missing</div>;
  }
  
  const stockSheet = {
    width: stockSheetProp.statistics?.sheet_size?.width || 0,
    length: stockSheetProp.statistics?.sheet_size?.length || 0,
    materialType: stockSheetProp.statistics?.materialType || 'Unknown'
  };
  
  // Calculate aspect ratio for scaling
  const baseViewBoxWidth = rotation === 90 ? stockSheet.length + 8 : stockSheet.width + 8;
  const baseViewBoxHeight = rotation === 90 ? stockSheet.width + 8 : stockSheet.length + 8;
  const aspectRatio = baseViewBoxHeight / baseViewBoxWidth;
  
  // Calculate viewBox with zoom and pan
  const viewBoxWidth = baseViewBoxWidth / zoom;
  const viewBoxHeight = baseViewBoxHeight / zoom;
  const viewBoxX = -1 + panX;
  const viewBoxY = -1 + panY;
  const panels = stockSheetProp.panels || [];
  const rawCuttingSequence = stockSheetProp.cutlist || [];
  const wastePanels = stockSheetProp.waste_panels || [];
  const statistics = stockSheetProp.statistics || {};
  const subsheets = stockSheetProp.subsheets || [];
  
  // Normalize cut data
  const normalizeCut = (cut) => {
    const position = cut.absolute_position ?? 0;
    const type = cut.cut_type ?? 'horizontal';
    
    // Calculate minX, maxX, minY, maxY if not provided
    let minX = cut.minX;
    let maxX = cut.maxX;
    let minY = cut.minY;
    let maxY = cut.maxY;
    
    if (minX === undefined || maxX === undefined || minY === undefined || maxY === undefined) {
      // Try to get subsheet bounds if this cut is performed on a subsheet
      let boundsX = 0, boundsY = 0, boundsWidth = stockSheet.width, boundsLength = stockSheet.length;
      
      if (cut.subsheet_id !== undefined && subsheets.length > 0) {
        const subsheet = subsheets.find(s => s.id === cut.subsheet_id);
        if (subsheet) {
          boundsX = subsheet.x_position ?? 0;
          boundsY = subsheet.y_position ?? 0;
          boundsWidth = subsheet.width ?? stockSheet.width;
          boundsLength = subsheet.length ?? stockSheet.length;
        }
      }
      
      if (type === 'vertical') {
        minX = position;
        maxX = position;
        minY = boundsY;
        maxY = boundsY + boundsLength;
      } else {
        minX = boundsX;
        maxX = boundsX + boundsWidth;
        minY = position;
        maxY = position;
      }
    }
    
    return {
      position,
      relativePosition: cut.relative_position ?? position,
      dimension: cut.cut_length ?? 0,
      type,
      minX,
      maxX,
      minY,
      maxY,
      ...cut // Include all other fields
    };
  };
  
  // Normalize all cuts
  const cuttingSequence = rawCuttingSequence.map(normalizeCut);
  
  // Helper function to check if a point/rectangle is within a subsheet
  // Uses a small tolerance for floating point precision
  const isWithinSubsheet = (x, y, width, length, subsheet) => {
    if (!subsheet) return true; // If no subsheet, don't grey out
    const subsheetX = subsheet.x_position || 0;
    const subsheetY = subsheet.y_position || 0;
    const subsheetWidth = subsheet.width || 0;
    const subsheetLength = subsheet.length || 0;
    const tolerance = 0.001; // Small tolerance for floating point comparisons
    
    // Check if the rectangle overlaps with or is within the subsheet
    // A rectangle is considered "within" if it overlaps at all with the subsheet
    const rectRight = x + width;
    const rectBottom = y + length;
    const subsheetRight = subsheetX + subsheetWidth;
    const subsheetBottom = subsheetY + subsheetLength;
    
    // Check for overlap (not strict containment)
    return (x + tolerance < subsheetRight && 
            rectRight - tolerance > subsheetX && 
            y + tolerance < subsheetBottom && 
            rectBottom - tolerance > subsheetY);
  };
  
  // Get the active subsheet when hovering over a cut
  const getActiveSubsheet = () => {
    if (hoveredCut === null || !cuttingSequence[hoveredCut]) return null;
    const cut = cuttingSequence[hoveredCut];
    
    // Use subsheet_id to find the subsheet this cut is being performed on
    if (cut.subsheet_id !== undefined && subsheets.length > 0) {
      const subsheet = subsheets.find(s => s.id === cut.subsheet_id);
      return subsheet || null;
    }
    
    return null;
  };
  
  const activeSubsheet = getActiveSubsheet();
  
  // Calculate used and wasted areas
  const sheetArea = stockSheet.width * stockSheet.length;
  // Helper function to get label for a panel by ID
  // Uses responsePanels which are kept in sync with input table labels via useEffect in App.js
  // Always returns a label - either the custom label or "Panel [id]"
  const getLabelForPanelId = (panelId) => {
    if (panelId === undefined || panelId === null) {
      return 'Panel';
    }
    
    // Ensure panelId is a number for consistent comparison
    const numericPanelId = typeof panelId === 'string' ? parseInt(panelId, 10) : panelId;
    if (isNaN(numericPanelId)) {
      return `Panel ${panelId}`;
    }
    
    // First, get the response panel to know what label it should have
    // This is the source of truth from the backend
    const responsePanel = panels.find(p => {
      const responseId = typeof p.id === 'string' ? parseInt(p.id, 10) : p.id;
      return !isNaN(responseId) && responseId === numericPanelId;
    });
    
    // Debug logging (can be removed in production)
    // if (process.env.NODE_ENV === 'development' && numericPanelId >= 9 && numericPanelId <= 16) {
    //   console.log(`[Label Debug] Panel ID ${numericPanelId}:`, {
    //     responsePanel: responsePanel ? { id: responsePanel.id, label: responsePanel.label } : null,
    //     inputPanelsCount: inputPanels.length,
    //     inputPanelsIds: inputPanels.map(p => ({ label: p.label, enabled: p.enabled, ids: p.ids }))
    //   });
    // }
    
    // Use response panel label if available
    // Labels are kept in sync with input table via useEffect in App.js
    if (responsePanel && responsePanel.label) {
      // if (process.env.NODE_ENV === 'development' && numericPanelId >= 9 && numericPanelId <= 16) {
      //   console.log(`[Label Debug] Panel ID ${numericPanelId}: Using responsePanel label "${responsePanel.label}"`);
      // }
      return responsePanel.label;
    }
    
    // Fallback: show panel ID
    // if (process.env.NODE_ENV === 'development' && numericPanelId >= 9 && numericPanelId <= 16) {
    //   console.log(`[Label Debug] Panel ID ${numericPanelId}: No label found, using fallback "Panel ${numericPanelId}"`);
    // }
    return `Panel ${numericPanelId}`;
  };

  // Helper function to get label for a panel (from placedPanel object)
  // Wrapper around getLabelForPanelId for convenience
  const getPanelLabel = (placedPanel) => {
    const actualPanel = placedPanel.panel || placedPanel;
    const panelId = actualPanel.id;
    return getLabelForPanelId(panelId);
  };

  // Generate consistent colors for panel variants
  // Uses label (from same source as displayed label) so panels with same label get same color
  // Maintains a label-to-color mapping to ensure unique colors for each label
  // Neon color palette
  const NEON_COLORS = [
    ' #ff006e', // 0 - Red - Neon Magenta
    ' #ff3366', // 1 - Red - Neon Pink
    ' #ff6600', // 2 - Orange - Bright Orange
    ' #ff9500', // 3 - Orange - Neon Orange
    ' #ffea00', // 4 - Yellow - Neon Yellow
    ' #00ff00', // 5 - Green - Pure Green
    ' #00ff41', // 6 - Green - Neon Green
    ' #00ff88', // 7 - Green - Neon Mint
    ' #66ff00', // 8 - Green - Lime Green
    ' #00d9ff', // 9 - Blue - Bright Cyan
    ' #00e5ff', // 10 - Blue - Neon Cyan
    ' #00ffcc', // 11 - Blue - Aqua
    ' #0099ff', // 12 - Blue - Bright Blue
    ' #9900ff', // 13 - Violet - Deep Purple
    ' #bf00ff', // 14 - Violet - Neon Purple
    ' #ff00ff', // 15 - Violet - Neon Fuchsia
  ];

  // Generate a consistent color key based on dimensions, label, and material type
  // Uses ONLY the backend response panels array as the source of truth
  const getColorKey = (placedPanel) => {
    if (!placedPanel) {
      return 'default';
    }
    
    const actualPanel = placedPanel.panel || placedPanel;
    const panelId = actualPanel.id;
    
    // Use only the response panels array from the JSON response
    if (panelId !== undefined && panelId !== null && responsePanels.length > 0) {
      const numericPanelId = typeof panelId === 'string' ? parseInt(panelId, 10) : panelId;
      
      if (!isNaN(numericPanelId)) {
        const responsePanel = responsePanels.find(p => {
          const responseId = typeof p.id === 'string' ? parseInt(p.id, 10) : p.id;
          return !isNaN(responseId) && responseId === numericPanelId;
        });
        
        if (responsePanel) {
          // Extract data directly from response panel
          const width = parseFloat(responsePanel.width) || 0;
          const length = parseFloat(responsePanel.length) || 0;
          const materialType = (responsePanel.material || responsePanel.materialType || '').trim();
          
          // Normalize dimensions so width <= length to handle rotation
          const dim1 = Math.min(width, length);
          const dim2 = Math.max(width, length);
          
          // Round dimensions to 4 decimal places for consistency
          const roundedDim1 = Math.round(dim1 * 10000) / 10000;
          const roundedDim2 = Math.round(dim2 * 10000) / 10000;
          
          // Create key: dim1 x dim2 _ material (label not included so color stays stable when label changes)
          const key = `${roundedDim1}x${roundedDim2}_${materialType}`;
          
          return key;
        }
      }
    }
    
    // If panel not found in response, return default key
    return 'default';
  };

  // Hash function to convert a string key to a consistent color index
  const hashStringToColorIndex = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    // Ensure positive index and map to color array
    return Math.abs(hash) % NEON_COLORS.length;
  };

  // Cache for color keys to ensure consistency
  const colorCacheRef = useRef(new Map());
  
  // Clear cache when responsePanels changes
  useEffect(() => {
    colorCacheRef.current.clear();
  }, [responsePanels]);

  const getColorForPanel = (panel) => {
    if (!panel) return nullPanelColor; // theme-aware color for null panels
    
    // Get the color key for this panel
    const key = getColorKey(panel);
    
    // Check cache first
    if (colorCacheRef.current.has(key)) {
      return colorCacheRef.current.get(key);
    }
    
    // Use hash function to get consistent color index
    const colorIndex = hashStringToColorIndex(key);
    const color = NEON_COLORS[colorIndex].trim();
    
    // Cache the color for this key
    colorCacheRef.current.set(key, color);
    
    return color;
  };
  
  const panelsArray = panels || [];
  
  const usedArea = panelsArray.reduce((sum, panel) => {
    if (!panel) return sum;
    return sum + (panel.width * panel.height || panel.width * panel.length);
  }, 0);
  const wastedArea = sheetArea - usedArea;
  const usedPercentage = ((usedArea / sheetArea) * 100).toFixed(1);
  const wastedPercentage = ((wastedArea / sheetArea) * 100).toFixed(1);
  const numWastePanels = wastePanels ? wastePanels.length : 0;

  // Zoom handlers
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 10)); // Max 10x zoom
  };
  
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.1)); // Min 0.1x zoom
  };
  
  const handleResetView = () => {
    setZoom(1);
    setPanX(0);
    setPanY(0);
  };
  
  // Pan handlers
  const handleMouseDown = (e) => {
    // Allow panning with middle mouse button, Ctrl/Cmd/Shift + left click, or when zoomed in
    const canPan = e.button === 1 || // Middle mouse button
                   (e.button === 0 && (e.ctrlKey || e.metaKey || e.shiftKey)) || // Modifier + left click
                   (e.button === 0 && zoom > 1); // Left click when zoomed in
    
    if (canPan) {
      setIsPanning(true);
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setPanStart({ 
          x: e.clientX, 
          y: e.clientY,
          panX: panX,
          panY: panY
        });
      }
      e.preventDefault();
      e.stopPropagation();
    }
  };
  
  const handleMouseMove = (e) => {
    if (isPanning && containerRef.current && panStart.x !== undefined) {
      const rect = containerRef.current.getBoundingClientRect();
      const currentViewBoxWidth = baseViewBoxWidth / zoom;
      const currentViewBoxHeight = baseViewBoxHeight / zoom;
      
      // Calculate delta in screen pixels
      const deltaScreenX = e.clientX - panStart.x;
      const deltaScreenY = e.clientY - panStart.y;
      
      // Convert screen delta to SVG coordinate delta
      const deltaX = (deltaScreenX / rect.width) * currentViewBoxWidth;
      const deltaY = (deltaScreenY / rect.height) * currentViewBoxHeight;
      
      setPanX(panStart.panX - deltaX);
      setPanY(panStart.panY - deltaY);
    }
  };
  
  const handleMouseUp = () => {
    setIsPanning(false);
  };
  
  // Wheel zoom handler
  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.1, Math.min(10, zoom * delta));
      
      // Zoom towards mouse position
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        // Calculate SVG coordinates at mouse position
        const currentViewBoxWidth = baseViewBoxWidth / zoom;
        const currentViewBoxHeight = baseViewBoxHeight / zoom;
        const currentViewBoxX = -1 + panX;
        const currentViewBoxY = -1 + panY;
        
        const svgX = currentViewBoxX + (mouseX / rect.width) * currentViewBoxWidth;
        const svgY = currentViewBoxY + (mouseY / rect.height) * currentViewBoxHeight;
        
        // Calculate new pan to keep the same point under the mouse
        const newViewBoxWidth = baseViewBoxWidth / newZoom;
        const newViewBoxHeight = baseViewBoxHeight / newZoom;
        const newViewBoxX = svgX - (mouseX / rect.width) * newViewBoxWidth;
        const newViewBoxY = svgY - (mouseY / rect.height) * newViewBoxHeight;
        
        setZoom(newZoom);
        setPanX(newViewBoxX + 1); // Adjust for the -1 offset in viewBox
        setPanY(newViewBoxY + 1);
      } else {
        setZoom(newZoom);
      }
    }
  };

  return (
    <Box sx={{ mb: 4, p: 2, position: 'relative' }}>
      <style>
        {`
          @keyframes drawLine {
            0% {
              stroke-dashoffset: var(--line-length);
            }
            100% {
              stroke-dashoffset: 0;
            }
          }
        `}
      </style>
      <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' }, color: textColor }}>
        {(() => {
          const quantity = stockSheetProp.statistics?.quantity || 1;
          const sheetIndices = stockSheetProp.statistics?.sheet_indices || [index + 1];
          if (quantity > 1 && sheetIndices.length > 1) {
            return `Sheet #${sheetIndices[0]}-#${sheetIndices[sheetIndices.length - 1]}`;
          }
          return `Sheet #${sheetIndices[0] || index + 1}`;
        })()}
      </Typography>
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, md: 3 }, 
        mt: 2 
      }}>
        {/* SVG Visualization */}
        <Box sx={{ 
          flex: 1, 
          height: { xs: svgMaxHeight, md: 'auto' },
          maxHeight: svgMaxHeight,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 0,
          width: '100%',
          px: { xs: 2, md: 0 },
          position: 'relative'
        }}>
          <Box 
            ref={containerRef}
            sx={{
              width: { xs: 'auto', md: '100%' },
              height: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              cursor: isPanning ? 'grabbing' : (zoom > 1 ? 'grab' : 'default'),
              // On small screens, constrain width based on height and aspect ratio
              ...(window.innerWidth < 900 && {
                width: `calc(${svgMaxHeight} * ${1 / aspectRatio})`,
                maxWidth: 'calc(100% - 32px)' // Account for padding
              })
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
            onContextMenu={(e) => e.preventDefault()}
          >
            <svg
              ref={svgRef}
              width="100%"
              height="100%"
              viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`}
              preserveAspectRatio="xMidYMid meet"
              style={{ 
                backgroundColor: bgColor, 
                display: 'block',
                width: '100%',
                height: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
                userSelect: 'none'
              }}
            >
          <defs>
            <filter id="neon-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <pattern id={`wastePattern-${index}-${themeMode}`} patternUnits="userSpaceOnUse" width="2" height="2">
              <path d="M-1,1 l2,-2 M0,2 l2,-2" stroke={wastePatternColor} strokeWidth="0.25" />
            </pattern>
          </defs>
          <g>
          {/* Highlight active sub-sheet when hovering - removed border, using opacity only */}
          
          {/* Draw each panel */}
          {panelsArray.map((placedPanel, idx) => {
            if (!placedPanel) return null;
            const baseX = placedPanel.x_position ?? placedPanel.x ?? 0;
            const baseY = placedPanel.y_position ?? placedPanel.y ?? 0;
            const baseWidth = placedPanel.width ?? 0;
            const baseLength = placedPanel.length ?? 0;
            
            const coords = transformCoordinates(baseX, baseY, baseWidth, baseLength, rotation, stockSheet);
            const { x, y, width, length } = coords;
            const centerX = x + width / 2;
            const centerY = y + length / 2;
            
            // Check if this panel is within the active subsheet (use original coordinates)
            const isInActiveRegion = activeSubsheet 
              ? isWithinSubsheet(baseX, baseY, baseWidth, baseLength, activeSubsheet)
              : true; // If no active subsheet, don't grey out
            
            const baseFontSize = Math.min(width, length) / 15;
            const dimensionFontSize = fontSize;  // Use prop value
            const labelFontSize = fontSize;
            
            // Display the ORIGINAL panel dimensions (not the placed dimensions)
            // input_width and input_length are always the panel's true dimensions
            const displayTopWidth = placedPanel.rotated 
              ? (placedPanel.input_length || placedPanel.originalLength || placedPanel.length)
              : (placedPanel.input_width || placedPanel.originalWidth || placedPanel.width);
            const displayLeftHeight = placedPanel.rotated 
              ? (placedPanel.input_width || placedPanel.originalWidth || placedPanel.width)
              : (placedPanel.input_length || placedPanel.originalLength || placedPanel.length);
            
            return (
              <g key={idx} opacity={activeSubsheet && !isInActiveRegion ? 0.25 : 1}>
                {(() => {
                  const neonColor = getColorForPanel(placedPanel.panel || placedPanel);
                  // Make gradient ID unique across all sheets, not just within one sheet
                  const gradientId = `panel-gradient-sheet${index}-panel${idx}`;
                  // Inset the border to create visible gaps between panels
                  const inset = 0.1;
                  const insetX = x + inset;
                  const insetY = y + inset;
                  const insetWidth = width - (2 * inset);
                  const insetHeight = length - (2 * inset);
                  
                  return (
                    <>
                      <defs>
                        <radialGradient id={gradientId} cx="50%" cy="50%" r="50%" gradientUnits="objectBoundingBox">
                          <stop offset="0%" stopColor={neonColor} stopOpacity="0.10" />
                          <stop offset="50%" stopColor={neonColor} stopOpacity="0.15" />
                          <stop offset="100%" stopColor={neonColor} stopOpacity="0.25" />
                        </radialGradient>
                      </defs>
                      <rect
                        x={insetX}
                        y={insetY}
                        width={insetWidth}
                        height={insetHeight}
                        rx="0.5"
                        ry="0.5"
                        fill={`url(#${gradientId})`}
                        stroke={neonColor}
                        strokeWidth="0.25"
                        onMouseEnter={() => setHoveredPanel(idx)}
                        onMouseLeave={() => setHoveredPanel(null)}
                        style={{ cursor: 'pointer' }}
                      />
                    </>
                  );
                })()}
                
                {showDimensions && (
                  <>
                    <text
                      x={centerX}
                      y={y + dimensionFontSize + 0.5}
                      textAnchor="middle"
                      fontSize={dimensionFontSize}
                      fontWeight="bold"
                      fill={textColor}
                      pointerEvents="none"
                    >
                      {formatDimension(displayTopWidth)}{getUnitSymbol(unitSystem)}
                    </text>
                    
                    <text
                      x={x + dimensionFontSize + 0.5}
                      y={centerY}
                      textAnchor="middle"
                      fontSize={dimensionFontSize}
                      fontWeight="bold"
                      fill={textColor}
                      transform={`rotate(-90 ${x + dimensionFontSize + 0.5} ${centerY})`}
                      pointerEvents="none"
                    >
                      {formatDimension(displayLeftHeight)}{getUnitSymbol(unitSystem)}
                    </text>
                  </>
                )}
                
                {showLabels && (() => {
                  // Rotate label if panel is significantly taller than wide (2:1 ratio or more)
                  const shouldRotateLabel = length / width >= 2;
                  const label = getPanelLabel(placedPanel);
                  
                  return (
                    <text
                      x={centerX}
                      y={centerY + labelFontSize / 3}
                      textAnchor="middle"
                      fontSize={labelFontSize}
                      fontWeight="bold"
                      fill={textColor}
                      pointerEvents="none"
                      transform={shouldRotateLabel ? `rotate(-90 ${centerX} ${centerY})` : undefined}
                    >
                      {label}
                    </text>
                  );
                })()}
                
                {placedPanel.rotated && showRotationIndicator && (
                  <g transform={`translate(${x + width - 3}, ${y + 1.5})`}>
                    <svg width={Math.max(2.5, stockSheet.width / 40)} height={Math.max(2.5, stockSheet.width / 40)} viewBox="0 0 512 512" fill="#f00">
                      <path d="M496 48V192c0 17.69-14.31 32-32 32H320c-17.69 0-32-14.31-32-32s14.31-32 32-32h63.39c-29.97-39.7-77.25-63.78-127.6-63.78C167.7 96.22 96 167.9 96 256s71.69 159.8 159.8 159.8c34.88 0 68.03-11.03 95.88-31.94c14.22-10.53 34.22-7.75 44.81 6.375c10.59 14.16 7.75 34.22-6.375 44.81c-39.03 29.28-85.36 44.86-134.2 44.86C132.5 479.9 32 379.4 32 256s100.5-223.9 223.9-223.9c69.15 0 134 31.91 176.1 86.12V48c0-17.69 14.31-32 32-32S496 30.31 496 48z"/>
                    </svg>
                  </g>
                )}
              </g>
            );
          })}
          
          {/* Draw waste area rectangles */}
          {wastePanels && wastePanels.map((waste, idx) => {
            const baseX = waste.x_position ?? waste.x ?? 0;
            const baseY = waste.y_position ?? waste.y ?? 0;
            const baseWidth = waste.width ?? 0;
            const baseLength = waste.length ?? 0;
            
            const coords = transformCoordinates(baseX, baseY, baseWidth, baseLength, rotation, stockSheet);
            const { x, y, width, length } = coords;
            
            // Check if this waste panel is within the active subsheet
            const isInActiveRegion = activeSubsheet 
              ? isWithinSubsheet(baseX, baseY, baseWidth, baseLength, activeSubsheet)
              : true; // If no active subsheet, don't grey out
            
            const wasteOpacity = activeSubsheet && !isInActiveRegion ? 0.25 : 1;
            
            return (
              <rect
                key={`waste-${idx}`}
                x={x}
                y={y}
                width={width}
                height={length}
                fill={`url(#wastePattern-${index}-${themeMode})`}
                stroke={wasteStrokeColor}
                strokeWidth="0.3"
                opacity={wasteOpacity}
                pointerEvents="all"
                onMouseEnter={() => setHoveredPanel(`waste-${idx}`)}
                onMouseLeave={() => setHoveredPanel(null)}
                style={{ cursor: 'pointer' }}
              />
            );
          })}
          
          {/* Draw black exterior border */}
          {(() => {
            const sheetDims = getSheetDimensions(rotation, stockSheet);
            return (
              <rect
                x={0}
                y={0}
                width={sheetDims.width}
                height={sheetDims.height}
                fill="none"
                stroke="#333"
                strokeWidth="0.5"
              />
            );
          })()}
          
          
          {/* Draw exterior dimension annotations when hovering over a panel or waste */}
          {hoveredPanel !== null && (() => {
            // Check if it's a waste area or a panel
            let baseX, baseY, baseWidth, baseLength;
            
            if (typeof hoveredPanel === 'string' && hoveredPanel.startsWith('waste-')) {
              // Find the waste area from backend data
              const wasteIdx = parseInt(hoveredPanel.split('-')[1]);
              
              if (!wastePanels || !wastePanels[wasteIdx]) return null;
              
              const waste = wastePanels[wasteIdx];
              baseX = waste.x_position ?? waste.x ?? 0;
              baseY = waste.y_position ?? waste.y ?? 0;
              baseWidth = waste.width ?? 0;
              baseLength = waste.length ?? 0;
            } else {
              // It's a panel
              if (!panels[hoveredPanel]) return null;
              const panel = panels[hoveredPanel];
              baseX = panel.x_position ?? panel.x ?? 0;
              baseY = panel.y_position ?? panel.y ?? 0;
              baseWidth = panel.width ?? 0;
              baseLength = panel.length ?? 0;
            }
            
            const coords = transformCoordinates(baseX, baseY, baseWidth, baseLength, rotation, stockSheet);
            const { x, y, width, length } = coords;
            
            const sheetDims = getSheetDimensions(rotation, stockSheet);
            const sheetWidth = sheetDims.width;
            const sheetHeight = sheetDims.height;
            
            const dimFontSize = fontSize; // Use the same fontSize as panel labels
            const offset = 2; // Distance from edge
            const lineExtension = 1; // How far lines extend beyond the edge
            
            return (
              <g>
                {/* Bottom dimension line (width of the panel) */}
                <line
                  x1={x}
                  y1={sheetHeight + offset}
                  x2={x + width}
                  y2={sheetHeight + offset}
                  stroke="#d32f2f"
                  strokeWidth="0.3"
                  pointerEvents="none"
                />
                <line
                  x1={x}
                  y1={sheetHeight + offset - lineExtension}
                  x2={x}
                  y2={sheetHeight + offset + lineExtension}
                  stroke="#d32f2f"
                  strokeWidth="0.3"
                  pointerEvents="none"
                />
                <line
                  x1={x + width}
                  y1={sheetHeight + offset - lineExtension}
                  x2={x + width}
                  y2={sheetHeight + offset + lineExtension}
                  stroke="#d32f2f"
                  strokeWidth="0.3"
                  pointerEvents="none"
                />
                <text
                  x={x + width / 2}
                  y={sheetHeight + offset + dimFontSize + 0.5}
                  textAnchor="middle"
                  fontSize={dimFontSize}
                  fontWeight="bold"
                  fill="#d32f2f"
                  pointerEvents="none"
                >
                  {formatDimension(width)}
                </text>
                
                {/* Right side dimension line (height of the panel) */}
                <line
                  x1={sheetWidth + offset}
                  y1={y}
                  x2={sheetWidth + offset}
                  y2={y + length}
                  stroke="#d32f2f"
                  strokeWidth="0.3"
                  pointerEvents="none"
                />
                <line
                  x1={sheetWidth + offset - lineExtension}
                  y1={y}
                  x2={sheetWidth + offset + lineExtension}
                  y2={y}
                  stroke="#d32f2f"
                  strokeWidth="0.3"
                  pointerEvents="none"
                />
                <line
                  x1={sheetWidth + offset - lineExtension}
                  y1={y + length}
                  x2={sheetWidth + offset + lineExtension}
                  y2={y + length}
                  stroke="#d32f2f"
                  strokeWidth="0.3"
                  pointerEvents="none"
                />
                <text
                  x={sheetWidth + offset + dimFontSize + 0.5}
                  y={y + length / 2}
                  textAnchor="middle"
                  fontSize={dimFontSize}
                  fontWeight="bold"
                  fill="#d32f2f"
                  transform={`rotate(-90 ${sheetWidth + offset + dimFontSize + 0.5} ${y + length / 2})`}
                  pointerEvents="none"
                >
                  {formatDimension(length)}
                </text>
              </g>
            );
          })()}
          
          {/* Cut Order Overlay - dashed lines (render FIRST) */}
          {showCutOrder && cuttingSequence.map((cut, idx) => {
            // Transform cut coordinates for rotation
            const coords = transformCutCoordinates(cut, rotation, stockSheet);
            const { x1, y1, x2, y2 } = coords;
            
            // Determine color based on cut type
            const isHorizontal = (rotation === 90 && cut.type === 'vertical') || (rotation === 0 && cut.type === 'horizontal');
            const lineColor = isHorizontal ? '#FFA500' : '#007fceff'; // Orange for horizontal, blue for vertical
            
            return (
              <line
                key={`cutorder-line-${idx}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={lineColor}
                strokeWidth="0.5"
                strokeDasharray="1,1"
                opacity="0.8"
                pointerEvents="none"
              />
            );
          })}
          
          {/* Draw cutting lines ON TOP - show grey waste lines always, red line when hovering */}
          {cuttingSequence.map((cut, idx) => {
            const isHovered = hoveredCut === idx;
            const showGreyOnly = hoveredCut === null; // Show grey lines when nothing is hovered
            const showRedLine = isHovered; // Show red line only when this cut is hovered
            
            // Helper function to check if a point is inside any panel
            const isInPanel = (x, y, tolerance = 0.1) => {
              return panels.some(pp => {
                const px = pp.x_position ?? pp.x ?? 0;
                const py = pp.y_position ?? pp.y ?? 0;
                return x >= px - tolerance && 
                       x <= px + pp.width + tolerance &&
                       y >= py - tolerance && 
                       y <= py + pp.length + tolerance;
              });
            };
            
            // Helper function to find waste segments along the cut line
            const findWasteSegments = (cut) => {
              const segments = [];
              const samplePoints = 50; // Number of points to sample along the cut
              
              if (cut.type === 'horizontal') {
                // Sample along X axis at the cut position Y
                const step = (cut.maxX - cut.minX) / samplePoints;
                let wasteStart = null;
                
                for (let i = 0; i <= samplePoints; i++) {
                  const x = cut.minX + (i * step);
                  const inPanel = isInPanel(x, cut.position);
                  
                  if (!inPanel && wasteStart === null) {
                    wasteStart = x;
                  } else if (inPanel && wasteStart !== null) {
                    segments.push({ start: wasteStart, end: x - step });
                    wasteStart = null;
                  }
                }
                
                // Close last segment if needed
                if (wasteStart !== null) {
                  segments.push({ start: wasteStart, end: cut.maxX });
                }
              } else {
                // Sample along Y axis at the cut position X
                const step = (cut.maxY - cut.minY) / samplePoints;
                let wasteStart = null;
                
                for (let i = 0; i <= samplePoints; i++) {
                  const y = cut.minY + (i * step);
                  const inPanel = isInPanel(cut.position, y);
                  
                  if (!inPanel && wasteStart === null) {
                    wasteStart = y;
                  } else if (inPanel && wasteStart !== null) {
                    segments.push({ start: wasteStart, end: y - step });
                    wasteStart = null;
                  }
                }
                
                // Close last segment if needed
                if (wasteStart !== null) {
                  segments.push({ start: wasteStart, end: cut.maxY });
                }
              }
              
              return segments;
            };
            
            // Get the subsheet this cut is being performed on
            const cutSubsheet = cut.subsheet_id !== undefined && subsheets.length > 0
              ? subsheets.find(s => s.id === cut.subsheet_id)
              : null;
            
            // Limit waste segment calculation to subsheet bounds
            const limitedCut = limitCutToSubsheet(cut, cutSubsheet, stockSheet);
            const wasteSegments = findWasteSegments(limitedCut);
            
            // Transform cut coordinates for rotation
            // When hovering, use the active subsheet bounds; otherwise use the cut's subsheet
            const subsheetToUse = (isHovered && activeSubsheet) ? activeSubsheet : cutSubsheet;
            const cutForTransform = limitCutToSubsheet(cut, subsheetToUse, stockSheet);
            const coords = transformCutCoordinates(cutForTransform, rotation, stockSheet, subsheetToUse);
            const { x1, y1, x2, y2 } = coords;
            
            const cutLength = Math.sqrt((x2 - x1)**2 + (y2 - y1)**2);
            
            return (
              <g key={`cut-${idx}-${hoveredCut}`}>
                {/* Draw grey lines for waste segments - always visible, clipped to subsheet bounds */}
                {wasteSegments.map((segment, segIdx) => {
                  let wx1, wy1, wx2, wy2;
                  
                  if (rotation === 90) {
                    if (cut.type === 'horizontal') {
                      wx1 = cut.position;
                      wy1 = stockSheet.width - segment.end;
                      wx2 = cut.position;
                      wy2 = stockSheet.width - segment.start;
                    } else {
                      wx1 = segment.start;
                      wy1 = stockSheet.width - cut.position;
                      wx2 = segment.end;
                      wy2 = stockSheet.width - cut.position;
                    }
                  } else {
                    if (cut.type === 'horizontal') {
                      wx1 = segment.start;
                      wy1 = cut.position;
                      wx2 = segment.end;
                      wy2 = cut.position;
                    } else {
                      wx1 = cut.position;
                      wy1 = segment.start;
                      wx2 = cut.position;
                      wy2 = segment.end;
                    }
                  }
                  
                  // Clip waste segments to subsheet bounds if subsheet exists
                  if (cutSubsheet) {
                    const subsheetX = cutSubsheet.x_position ?? 0;
                    const subsheetY = cutSubsheet.y_position ?? 0;
                    const subsheetWidth = cutSubsheet.width ?? 0;
                    const subsheetLength = cutSubsheet.length ?? 0;
                    
                    if (rotation === 90) {
                      if (cut.type === 'horizontal') {
                        // Clip Y coordinates
                        const subsheetStartY = stockSheet.width - (subsheetX + subsheetWidth);
                        const subsheetEndY = stockSheet.width - subsheetX;
                        wy1 = Math.max(subsheetStartY, Math.min(subsheetEndY, wy1));
                        wy2 = Math.max(subsheetStartY, Math.min(subsheetEndY, wy2));
                      } else {
                        // Clip X coordinates
                        const subsheetStartX = subsheetY;
                        const subsheetEndX = subsheetY + subsheetLength;
                        wx1 = Math.max(subsheetStartX, Math.min(subsheetEndX, wx1));
                        wx2 = Math.max(subsheetStartX, Math.min(subsheetEndX, wx2));
                      }
                    } else {
                      if (cut.type === 'horizontal') {
                        // Clip X coordinates
                        wx1 = Math.max(subsheetX, Math.min(subsheetX + subsheetWidth, wx1));
                        wx2 = Math.max(subsheetX, Math.min(subsheetX + subsheetWidth, wx2));
                      } else {
                        // Clip Y coordinates
                        wy1 = Math.max(subsheetY, Math.min(subsheetY + subsheetLength, wy1));
                        wy2 = Math.max(subsheetY, Math.min(subsheetY + subsheetLength, wy2));
                      }
                    }
                  }
                  
                  return (
                    <line
                      key={`waste-${idx}-${segIdx}`}
                      x1={wx1}
                      y1={wy1}
                      x2={wx2}
                      y2={wy2}
                      stroke={gridLineColor}
                      strokeWidth="0.5"
                      opacity="0.5"
                      pointerEvents="none"
                    />
                  );
                })}
                
                {/* Draw main red cutting line - only when hovered */}
                {showRedLine && (
                  <line
                    key={`line-${idx}-${hoveredCut}`}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke={cutLineColor}
                    strokeWidth="0.50"
                    strokeDasharray={`${cutLength} ${cutLength}`}
                    strokeDashoffset={cutLength}
                    opacity="1"
                    style={{ 
                      cursor: 'pointer',
                      '--line-length': cutLength,
                      animation: 'drawLine 0.5s ease-out forwards'
                    }}
                  />
                )}
                
                {/* Cut Order Numbers - render AFTER red line */}
                {showCutOrder && (() => {
                  // Determine color based on cut type
                  const isHorizontal = (rotation === 90 && cut.type === 'vertical') || (rotation === 0 && cut.type === 'horizontal');
                  const lineColor = isHorizontal ? '#FFA500' : '#007fceff'; // Orange for horizontal, blue for vertical
                  const textColor = hoveredCut === idx ? '#ff0000' : lineColor; // Red when hovered, otherwise line color
                  
                  // Calculate position for the number label based on cut direction
                  let labelX, labelY;
                  if (isHorizontal) {
                    // Horizontal cuts: position label below the cut line
                    labelX = x1 + 0.5;
                    labelY = y1 - 1;
                  } else {
                    // Vertical cuts: position label to the right of the cut line
                    labelX = x1 - 0.5;
                    labelY = y1 - 3;
                  }
                  
                  // Ensure label stays within bounds
                  const sheetWidth = rotation === 90 ? stockSheet.length : stockSheet.width;
                  const sheetHeight = rotation === 90 ? stockSheet.width : stockSheet.length;
                  const clampedLabelX = Math.max(0.5, Math.min(labelX, sheetWidth - fontSize));
                  const clampedLabelY = Math.max(fontSize, Math.min(labelY + fontSize, sheetHeight));
                  
                  return (
                    <g key={`cutorder-num-${idx}`}>
                      {/* Outline for visibility */}
                      <text
                        x={clampedLabelX}
                        y={clampedLabelY}
                        fontSize={fontSize}
                        fill="none"
                        stroke={isDark ? '#000000' : '#ffffff'}
                        strokeWidth="0.4"
                        fontWeight="bold"
                        pointerEvents="none"
                        textAnchor="start"
                      >
                        {idx + 1}
                      </text>
                      {/* Colored text - changes to red on hover */}
                      <text
                        x={clampedLabelX}
                        y={clampedLabelY}
                        fontSize={fontSize}
                        fill={textColor}
                        fontWeight="bold"
                        pointerEvents="none"
                        textAnchor="start"
                      >
                        {idx + 1}
                      </text>
                    </g>
                  );
                })()}
              </g>
            );
          })}
          </g>
        </svg>
          </Box>
        </Box>
        
        {/* Hidden SVG for PDF export - without the extra 8 units for hover dimensions */}
        <Box sx={{ display: 'none' }}>
          <svg
            data-for-pdf="true"
            width="100%"
            viewBox={rotation === 90 ? `0 0 ${stockSheet.length} ${stockSheet.width}` : `0 0 ${stockSheet.width} ${stockSheet.length}`}
            preserveAspectRatio="xMidYMid meet"
            style={{ backgroundColor: '#f9f9f9', display: 'block' }}
          >
          <defs>
            <pattern id="wastePattern-pdf" patternUnits="userSpaceOnUse" width="2" height="2">
              <path d="M-1,1 l2,-2 M0,2 l2,-2" stroke="#cccccc" strokeWidth="0.25" />
            </pattern>
          </defs>
          <g>
          {/* Draw each panel */}
          {panelsArray.map((placedPanel, idx) => {
            if (!placedPanel) return null;
            const baseX = placedPanel.x_position ?? placedPanel.x ?? 0;
            const baseY = placedPanel.y_position ?? placedPanel.y ?? 0;
            const baseWidth = placedPanel.width ?? 0;
            const baseLength = placedPanel.length ?? 0;
            
            const coords = transformCoordinates(baseX, baseY, baseWidth, baseLength, rotation, stockSheet);
            const { x, y, width, length } = coords;
            const centerX = x + width / 2;
            const centerY = y + length / 2;
            
            const baseFontSize = Math.min(width, length) / 15;
            const dimensionFontSize = fontSize;
            const labelFontSize = fontSize;
            
            const displayTopWidth = placedPanel.rotated 
              ? (placedPanel.input_length || placedPanel.originalLength || placedPanel.length)
              : (placedPanel.input_width || placedPanel.originalWidth || placedPanel.width);
            const displayLeftHeight = placedPanel.rotated 
              ? (placedPanel.input_width || placedPanel.originalWidth || placedPanel.width)
              : (placedPanel.input_length || placedPanel.originalLength || placedPanel.length);
            
            return (
              <g key={idx}>
                {(() => {
                  const neonColor = getColorForPanel(placedPanel.panel || placedPanel);
                  // Make gradient ID unique across all sheets for PDF export
                  const gradientId = `panel-gradient-pdf-sheet${index}-panel${idx}`;
                  // Inset the border to create visible gaps between panels
                  const inset = 0.1;
                  const insetX = x + inset;
                  const insetY = y + inset;
                  const insetWidth = width - (2 * inset);
                  const insetHeight = length - (2 * inset);
                  
                  return (
                    <>
                      <defs>
                        <radialGradient id={gradientId} cx="50%" cy="50%" r="50%" gradientUnits="objectBoundingBox">
                          <stop offset="0%" stopColor={neonColor} stopOpacity="0.08" />
                          <stop offset="60%" stopColor={neonColor} stopOpacity="0.12" />
                          <stop offset="100%" stopColor={neonColor} stopOpacity="0.25" />
                        </radialGradient>
                      </defs>
                      <rect
                        x={insetX}
                        y={insetY}
                        width={insetWidth}
                        height={insetHeight}
                        rx="0.5"
                        ry="0.5"
                        fill={`url(#${gradientId})`}
                        stroke={neonColor}
                        strokeWidth="0.25"
                      />
                    </>
                  );
                })()}
                
                {showDimensions && (
                  <>
                    <text
                      x={centerX}
                      y={y + dimensionFontSize + 0.5}
                      textAnchor="middle"
                      fontSize={dimensionFontSize}
                      fontWeight="bold"
                      fill={pdfTextColor}
                      pointerEvents="none"
                    >
                      {formatDimension(displayTopWidth)}{getUnitSymbol(unitSystem)}
                    </text>
                    
                    <text
                      x={x + dimensionFontSize + 0.5}
                      y={centerY}
                      textAnchor="middle"
                      fontSize={dimensionFontSize}
                      fontWeight="bold"
                      fill={pdfTextColor}
                      transform={`rotate(-90 ${x + dimensionFontSize + 0.5} ${centerY})`}
                      pointerEvents="none"
                    >
                      {formatDimension(displayLeftHeight)}{getUnitSymbol(unitSystem)}
                    </text>
                  </>
                )}
                
                {showLabels && (() => {
                  const label = getPanelLabel(placedPanel);
                  const shouldRotateLabel = length / width >= 2;
                  
                  return (
                    <text
                      x={centerX}
                      y={centerY + labelFontSize / 3}
                      textAnchor="middle"
                      fontSize={labelFontSize}
                      fontWeight="bold"
                      fill={pdfTextColor}
                      pointerEvents="none"
                      transform={shouldRotateLabel ? `rotate(-90 ${centerX} ${centerY})` : undefined}
                    >
                      {label}
                    </text>
                  );
                })()}
                
                {placedPanel.rotated && showRotationIndicator && (
                  <g transform={`translate(${x + width - 3}, ${y + 1.5})`}>
                    <svg width={Math.max(2.5, stockSheet.width / 40)} height={Math.max(2.5, stockSheet.width / 40)} viewBox="0 0 512 512" fill="#f00">
                      <path d="M496 48V192c0 17.69-14.31 32-32 32H320c-17.69 0-32-14.31-32-32s14.31-32 32-32h63.39c-29.97-39.7-77.25-63.78-127.6-63.78C167.7 96.22 96 167.9 96 256s71.69 159.8 159.8 159.8c34.88 0 68.03-11.03 95.88-31.94c14.22-10.53 34.22-7.75 44.81 6.375c10.59 14.16 7.75 34.22-6.375 44.81c-39.03 29.28-85.36 44.86-134.2 44.86C132.5 479.9 32 379.4 32 256s100.5-223.9 223.9-223.9c69.15 0 134 31.91 176.1 86.12V48c0-17.69 14.31-32 32-32S496 30.31 496 48z"/>
                    </svg>
                  </g>
                )}
              </g>
            );
          })}
          
          {/* Draw waste area rectangles */}
          {wastePanels && wastePanels.map((waste, idx) => {
            const baseX = waste.x_position ?? waste.x ?? 0;
            const baseY = waste.y_position ?? waste.y ?? 0;
            const baseWidth = waste.width ?? 0;
            const baseLength = waste.length ?? 0;
            
            const coords = transformCoordinates(baseX, baseY, baseWidth, baseLength, rotation, stockSheet);
            const { x, y, width, length } = coords;
            
            return (
              <rect
                key={`waste-${idx}`}
                x={x}
                y={y}
                width={width}
                height={length}
                fill="url(#wastePattern-pdf)"
                stroke="#999"
                strokeWidth="0.3"
              />
            );
          })}
          
          {/* Cut Order Overlay - dashed lines (render FIRST) */}
          {showCutOrder && cuttingSequence.map((cut, idx) => {
            // Transform cut coordinates for rotation
            const coords = transformCutCoordinates(cut, rotation, stockSheet);
            const { x1, y1, x2, y2 } = coords;
            
            // Determine color based on cut type
            const isHorizontal = (rotation === 90 && cut.type === 'vertical') || (rotation === 0 && cut.type === 'horizontal');
            const lineColor = isHorizontal ? '#FFA500' : '#007fceff'; // Orange for horizontal, blue for vertical
            
            return (
              <line
                key={`cutorder-pdf-line-${idx}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={lineColor}
                strokeWidth="0.5"
                strokeDasharray="1,1"
                opacity="0.8"
                pointerEvents="none"
              />
            );
          })}
          
          {/* Cut Order Numbers - render AFTER dashed lines */}
          {showCutOrder && cuttingSequence.map((cut, idx) => {
            // Transform cut coordinates for rotation
            const coords = transformCutCoordinates(cut, rotation, stockSheet);
            const { x1, y1, x2, y2 } = coords;
            
            // Determine color based on cut type
            const isHorizontal = (rotation === 90 && cut.type === 'vertical') || (rotation === 0 && cut.type === 'horizontal');
            const lineColor = isHorizontal ? '#FFA500' : '#007fceff'; // Orange for horizontal, blue for vertical
            
            // Calculate position for the number label based on cut direction
            let labelX, labelY;
            if (isHorizontal) {
              // Horizontal cuts: position label below the cut line
              labelX = x1 + 0.5;
              labelY = y1 - 1;
            } else {
              // Vertical cuts: position label to the right of the cut line
              labelX = x1 - 0.5;
              labelY = y1 - 3;
            }
            
            // Ensure label stays within bounds
            const sheetDims = getSheetDimensions(rotation, stockSheet);
            const sheetWidth = sheetDims.width;
            const sheetHeight = sheetDims.height;
            const clampedLabelX = Math.max(0, Math.min(labelX, sheetWidth - fontSize));
            const clampedLabelY = Math.max(fontSize, Math.min(labelY + fontSize, sheetHeight));
            
            return (
              <g key={`cutorder-pdf-num-${idx}`}>
                {/* Black outline for visibility */}
                <text
                  x={clampedLabelX}
                  y={clampedLabelY}
                  fontSize={fontSize}
                  fill="none"
                  stroke="#000000"
                  strokeWidth="0.3"
                  fontWeight="bold"
                  pointerEvents="none"
                  textAnchor="start"
                >
                  {idx + 1}
                </text>
                {/* Colored text on bottom */}
                <text
                  x={clampedLabelX}
                  y={clampedLabelY}
                  fontSize={fontSize}
                  fill={lineColor}
                  fontWeight="bold"
                  pointerEvents="none"
                  textAnchor="start"
                >
                  {idx + 1}
                </text>
              </g>
            );
          })}
          
          {/* Draw black exterior border */}
          {(() => {
            const sheetDims = getSheetDimensions(rotation, stockSheet);
            return (
              <rect
                x={0}
                y={0}
                width={sheetDims.width}
                height={sheetDims.height}
                fill="none"
                stroke="#333"
                strokeWidth="0.5"
              />
            );
          })()}
          </g>
          </svg>
        </Box>
      </Box>
    </Box>
  );
};

export default CutLayoutVisualization;


